import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, TextField } from "@mui/material";
import {
	AttachedFiles,
	AttachFile,
	DesignerNumberText,
	JobNameOptionalText,
	OrderDetailsText,
	PONumberText,
	RequiredFieldsText,
	UnableToDetermineFiles
} from "constants/text";
import { Controller, useFormContext } from "react-hook-form";
import {
	JOB_NAME_MAX_LENGTH,
	PO_NUMBER_MAX_LENGTH,
	schema
} from "components/NewOrders/NewOrderShippingDetailsForm/schema";
import { getFormFieldProps } from "utils/form";
import {
	BusinessPurposeSubtitleText,
	BusinessPurposeWrapper,
	JobNamePoNumberWrapper,
	LowesFileUploadSubheader,
	LowesStyledUploadButton,
	NewOrdersDesignersWrapper,
	NewOrdersStaticDesignerWrapper,
	RequiredFieldsTextWrapper,
	RequiredFieldWrapper,
	ShippingDetailsCard
} from "./NewOrders.styles";
import { useCallback, useEffect, useState } from "react";
import {
	BusinessPurposeAptOrDuplexText,
	BusinessPurposeErrorText,
	BusinessPurposeNewHouseText,
	BusinessPurposeRepairRemodelText,
	BusinessPurposeStockOrderText,
	BusinessPurposeTitleText,
	BusinessPurposeUnknownText,
	DesignerConnectionHeaderShippingDetails,
	DesignerIsNotHereText,
	EnterDesignerHelperText,
	NewOrderDesignerNumberText,
	NewOrderEnterDesignerNumberText,
	OptionalText,
	RequiredText
} from "./constants";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { models } from "types/api/viewModels";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { OrderBusinessPurpose, OrderType } from "types/declarations/enums.ts";
import useNewOrderDesigners from "hooks/useNewOrderDesigners";
import DesignerFormAlert from "./DesignerFormAlert/DesignerFormAlert";
import { components } from "types/declarations/api";
import HelperText from "../Form/HelperText/HelperText.tsx";
import UploadModal, { ErrorableFileState } from "../UploadModal/UploadModal.tsx";
import { cartActions, ReplacementFile } from "features/reducers/replacementOrder/cart.ts";
import UploadFile from "@mui/icons-material/UploadFile";
import styles from "pages/replacements-page-styles.module.css";
import UploadView from "../UploadView/UploadView.tsx";
import { sanitizeBlobName } from "utils/file.ts";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { designerOptionsWithCustomDesignerText } from "utils/designer.ts";
import { FileErrorTextContainer } from "components/Replacements/components/SelectReplacementReasonAndProblemDropdowns/SelectReplacementReasonAndProblemDropdowns.styles.ts";

interface OrderDetailsDefaultValues {
	poNumber?: string;
	jobName?: string;
	businessPurpose?: components["schemas"]["OrderBusinessPurpose"];
}

export interface ShippingOrderDetailsProp {
	defaultValues?: OrderDetailsDefaultValues;
	staticDesigner?: models["DesignerViewModel"] | string;
	isLowesUser?: boolean;
}

const businessPurposeOptions = [
	{ value: "REPAIR_REMODEL", label: BusinessPurposeRepairRemodelText, testId: "repair-remodel" },
	{ value: "NEW_CONSTRUCTION_SF", label: BusinessPurposeNewHouseText, testId: "new-construction" },
	{ value: "MULTI_FAMILY_APARTMENTS", label: BusinessPurposeAptOrDuplexText, testId: "multi-family-apts" },
	{ value: "STOCK_ORDER", label: BusinessPurposeStockOrderText, testId: "stock-order" },
	{ value: "UNKNOWN", label: BusinessPurposeUnknownText, testId: "unknown" }
] as const;

const ShippingOrderDetails = ({ defaultValues, staticDesigner, isLowesUser }: ShippingOrderDetailsProp) => {
	const dummyItem = { id: "lowes-upload" } as any;
	const maxFiles = 3;

	const { formState, register, watch, control, setValue, clearErrors, getValues } = useFormContext();
	const { errors } = formState;
	const dispatch = useDispatch();

	const currentOrder = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const replacementCartItem = useSelector((state: RootState) =>
		state.cart?.replacements?.find((item) => item.id === dummyItem.id)
	);

	const poNumberInput = watch("poNumber") ?? "";
	const jobNameInput = watch("jobName") ?? "";
	const designer = watch("designer");

	const staticDesignerHyphen =
		typeof staticDesigner === "object" && (staticDesigner.firstName || staticDesigner.lastName) ? " - " : "";
	const [open, setOpen] = useState(false);
	const [fileUploads, setFileUploads] = useState<ReplacementFile[]>([]);
	const [fileUploadHasError, setFileUploadHasError] = useState(false);
	const formFieldData = { formState, register, schema };
	const [hasInitializedFiles, setHasInitializedFiles] = useState(false);

	const {
		setDefaultDesigner,
		clearDesigner,
		addDesignerNumberToOrder,
		designerOptions,
		isLoadingDesigners,
		isCustomDesigner,
		setIsCustomDesigner
	} = useNewOrderDesigners();

	const checkFiles = useCallback(async () => {
		if (hasInitializedFiles || fileUploads.length === 0) {
			if (fileUploadHasError) {
				setFileUploadHasError(false);
			}
			return;
		}

		try {
			await Promise.all(fileUploads.map((file) => fetch(file.fileUrl)));
			if (fileUploadHasError) {
				setFileUploadHasError(false);
			}
		} catch {
			if (!fileUploadHasError) {
				setFileUploadHasError(true);
			}
		}
	}, [fileUploads, fileUploadHasError, hasInitializedFiles]);

	const handleClose = (files: ErrorableFileState[]) => {
		const validFiles = files.map(({ file }, index) => ({
			name: sanitizeBlobName(file.name),
			size: file.size,
			id: `${file.name}-${index}`,
			fileUrl: URL.createObjectURL(file),
			originalLineItemId: ""
		}));

		if (replacementCartItem) {
			dispatch(
				cartActions.updateItem({
					...replacementCartItem,
					cabinetFileUploads: [...(replacementCartItem.cabinetFileUploads ?? []), ...validFiles]
				})
			);
		} else {
			dispatch(
				cartActions.updateItem({
					id: dummyItem.id,
					cabinetFileUploads: validFiles
				})
			);
		}

		setFileUploadHasError(false);
		setHasInitializedFiles(false);
		setFileUploads((prev) => [...prev, ...validFiles]);
		setOpen(false);
	};

	const removeFile = (index: number) => {
		const newFiles = fileUploads.filter((_, i) => i !== index);
		setFileUploads(newFiles);

		if (replacementCartItem) {
			dispatch(
				cartActions.updateItem({
					...replacementCartItem,
					cabinetFileUploads: newFiles
				})
			);
		}
	};

	const handleDesignerChange = (_event: any, value: models["DesignerViewModel"] | string | null) => {
		if (value === DesignerIsNotHereText) {
			setIsCustomDesigner(true);
		} else if (value === null) {
			clearDesigner();
		} else {
			setIsCustomDesigner(false);
			setValue("customDesigner", undefined);
			clearErrors("designer");
			clearErrors("customDesigner");
			addDesignerNumberToOrder(typeof value === "object" ? (value?.designerNumber ?? null) : null);
		}
	};

	const getDesignerLabel = (option: models["DesignerViewModel"] | string) => {
		if (typeof option === "string") {
			return option;
		}
		const fullName = [option.firstName, option.lastName].filter(Boolean).join(" ");
		return [fullName, option.designerNumber].filter(Boolean).join(" - ");
	};

	useEffect(() => {
		if (replacementCartItem && replacementCartItem.cabinetFileUploads.length > 0) {
			setFileUploads(replacementCartItem.cabinetFileUploads ?? []);
			setHasInitializedFiles(true);
		}
	}, [setFileUploads, replacementCartItem]);

	useEffect(() => {
		if (isLowesUser) {
			const hasValidFiles = fileUploads.length > 0 && !fileUploadHasError;
			setValue("lowesFileUploads", hasValidFiles || null);

			if (hasValidFiles) {
				clearErrors("lowesFileUploads");
			}
		}
	}, [isLowesUser, fileUploads.length, fileUploadHasError, setValue, clearErrors]);

	useEffect(() => {
		setValue("businessPurpose", defaultValues?.businessPurpose ?? currentOrder?.businessPurpose);
	}, [currentOrder?.businessPurpose, setValue, defaultValues?.businessPurpose]);

	useEffect(() => {
		setValue("poNumber", defaultValues?.poNumber);
		setValue("jobName", defaultValues?.jobName);
	}, [defaultValues?.poNumber, defaultValues?.jobName, setValue, isLowesUser]);

	useEffect(() => {
		if (designer !== DesignerIsNotHereText) {
			clearErrors("customDesigner");
			setIsCustomDesigner(false);
		}
	}, [designer, clearErrors, setIsCustomDesigner]);

	useEffect(() => {
		setDefaultDesigner();
	}, [setDefaultDesigner]);

	useEffect(() => {
		(async () => {
			await checkFiles();
		})();
	}, [checkFiles]);

	useEffect(() => {
		if (
			fileUploadHasError &&
			replacementCartItem?.cabinetFileUploads &&
			replacementCartItem?.cabinetFileUploads.length > 0 &&
			!hasInitializedFiles
		) {
			setFileUploads([]);
			dispatch(cartActions.resetCart());
		}
	}, [fileUploadHasError, replacementCartItem?.cabinetFileUploads, dispatch, hasInitializedFiles]);

	return (
		<ShippingDetailsCard>
			<div data-testid="new-orders-shipping-order-details-header">{OrderDetailsText}</div>
			{(designer === DesignerIsNotHereText || isCustomDesigner) && (
				<DesignerFormAlert header={DesignerConnectionHeaderShippingDetails} />
			)}
			{!staticDesigner && <RequiredFieldsTextWrapper>{RequiredFieldsText}</RequiredFieldsTextWrapper>}
			<NewOrdersDesignersWrapper isCustomDesigner={designer === DesignerIsNotHereText}>
				{staticDesigner && (
					<NewOrdersStaticDesignerWrapper>
						<span>{DesignerNumberText}</span>
						<span style={{ marginBottom: 16 }}>
							{typeof staticDesigner === "string"
								? staticDesigner
								: [staticDesigner.firstName, staticDesigner.lastName].filter(Boolean).join(" ") +
									staticDesignerHyphen +
									staticDesigner.designerNumber}
						</span>
						<RequiredFieldsTextWrapper>{RequiredFieldsText}</RequiredFieldsTextWrapper>
					</NewOrdersStaticDesignerWrapper>
				)}
				{!staticDesigner && (
					<Controller
						name="designer"
						control={control}
						render={({ field: { onChange, value = null }, fieldState: { error } }) => (
							<AutoComplete
								options={Array.from(new Set([...designerOptions, DesignerIsNotHereText]))}
								onChange={(_event, value) => {
									onChange(value);
									handleDesignerChange(_event, value);
								}}
								getOptionLabel={getDesignerLabel}
								filterOptions={designerOptionsWithCustomDesignerText}
								value={isCustomDesigner ? DesignerIsNotHereText : value}
								isError={!!error}
								errorText={error?.message}
								label={`${NewOrderDesignerNumberText} ${designer === DesignerIsNotHereText ? RequiredText : OptionalText}`}
								dataTestId="shipping-order-details-designer"
								isLoading={isLoadingDesigners}
							/>
						)}
					/>
				)}
				{(designer === DesignerIsNotHereText || isCustomDesigner) && (
					<div>
						<TextField
							{...getFormFieldProps({
								name: "customDesigner",
								...formFieldData
							})}
							label={NewOrderEnterDesignerNumberText}
							data-testid="shipping-order-details-customDesigner"
							slotProps={{ htmlInput: { maxLength: 6 } }}
						/>
						{!errors.customDesigner?.message && <FormHelperText>{EnterDesignerHelperText}</FormHelperText>}
					</div>
				)}
			</NewOrdersDesignersWrapper>
			<JobNamePoNumberWrapper>
				<TextField
					{...getFormFieldProps({
						name: "poNumber",
						characterLimit: {
							currentLength: poNumberInput.length,
							limit: PO_NUMBER_MAX_LENGTH
						},
						...formFieldData
					})}
					slotProps={{ inputLabel: { shrink: Boolean(poNumberInput) || undefined } }}
					data-testid="shipping-order-details-poNumber"
					fullWidth
					label={PONumberText}
					size="small"
				/>
				<TextField
					{...getFormFieldProps({
						name: "jobName",
						characterLimit: {
							currentLength: jobNameInput.length,
							limit: JOB_NAME_MAX_LENGTH
						},
						...formFieldData
					})}
					slotProps={{ inputLabel: { shrink: Boolean(jobNameInput) || undefined } }}
					fullWidth
					data-testid="shipping-order-details-jobName"
					label={JobNameOptionalText}
					size="small"
				/>
			</JobNamePoNumberWrapper>
			{isLowesUser && (
				<>
					<RequiredFieldWrapper>Please attach Lowe’s XP form.*</RequiredFieldWrapper>
					{fileUploads.length < maxFiles && (
						<LowesStyledUploadButton
							variant="outlined"
							onClick={() => setOpen(true)}
							disabled={fileUploads.length === maxFiles}
							startIcon={<UploadFile />}
						>
							{AttachFile}
						</LowesStyledUploadButton>
					)}
					<LowesFileUploadSubheader>Supported Files: JPG, JPEG, PNG, PDF</LowesFileUploadSubheader>
					<LowesFileUploadSubheader>Max File Size: 10MB per image</LowesFileUploadSubheader>
					{((!!fileUploads.length && fileUploads.length > 0) || fileUploadHasError) && (
						<>
							{fileUploadHasError ? (
								<FileErrorTextContainer>
									<ErrorOutline aria-label="add-line-items" />
									<FormHelperText>{UnableToDetermineFiles}</FormHelperText>
								</FileErrorTextContainer>
							) : (
								<div className={styles.imageUploadViewContainer}>
									<p>{AttachedFiles}</p>
									{fileUploads?.map((fileUrl, index) => (
										<UploadView
											key={fileUrl.id}
											index={index}
											file={fileUrl}
											onImageRemove={() => removeFile(index)}
										/>
									))}
								</div>
							)}
						</>
					)}
					{errors.lowesFileUploads?.message && (
						<FileErrorTextContainer>
							<ErrorOutline aria-label="file-upload-error" />
							<FormHelperText error>{String(errors.lowesFileUploads.message)}</FormHelperText>
						</FileErrorTextContainer>
					)}
				</>
			)}
			{currentOrder?.orderType && currentOrder.orderType === OrderType.ORIGINAL && (
				<BusinessPurposeWrapper>
					<BusinessPurposeSubtitleText>{BusinessPurposeTitleText}</BusinessPurposeSubtitleText>
					<FormControl>
						<Controller
							name="businessPurpose"
							control={control}
							render={({ field: { onChange }, fieldState: { error } }) => (
								<RadioGroup
									row
									onChange={(_, value) => onChange(value)}
									sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}
								>
									{businessPurposeOptions.map(({ value, label, testId }) => (
										<FormControlLabel
											key={value}
											value={value}
											checked={OrderBusinessPurpose[value] === getValues("businessPurpose")}
											control={<Radio size="small" />}
											label={<span className="body1">{label}</span>}
											className="body1"
											data-testid={`order-purpose-${testId}-radioButton`}
										/>
									))}
									{!!error && (
										<HelperText
											isError={!!error}
											text={BusinessPurposeErrorText}
										/>
									)}
								</RadioGroup>
							)}
						/>
					</FormControl>
				</BusinessPurposeWrapper>
			)}
			<UploadModal
				handleClose={handleClose}
				item={dummyItem}
				open={open}
				maxFiles={maxFiles - fileUploads.length || 0}
				fileUploads={fileUploads}
			/>
		</ShippingDetailsCard>
	);
};

export default ShippingOrderDetails;
